import { useRef, useState } from 'react';
import { Eye, History, Loader2, Trash2, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useVersionStore } from '../../stores/versionStore';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { formatListStamp } from '../../utils/versionText';
import ConfirmDialog from '../ConfirmDialog';
import { toast } from '../../lib/toast';

function Entry({ v, onView, onDelete }) {
  const stamp = formatListStamp(v.created_at);
  const title = v.kind === 'named' ? v.label : stamp;
  return (
    <li className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-surface-2">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-ink truncate">{title}</div>
        <div className="text-[11px] text-ink-4">
          {v.kind === 'named' ? stamp : v.label ? <span className="text-warn">{v.label}</span> : 'automatisch'}
        </div>
      </div>
      <button type="button" aria-label={`Ansehen: ${title}`} onClick={() => onView(v)} className="p-1.5 text-ink-3 hover:text-brand rounded"><Eye size={14} /></button>
      <button type="button" aria-label={`Löschen: ${title}`} onClick={() => onDelete(v)} className="p-1.5 text-ink-3 hover:text-err rounded"><Trash2 size={14} /></button>
    </li>
  );
}

export default function VersionsPanel() {
  const { panelOpen, versions, loading, error } = useVersionStore(useShallow((s) => ({
    panelOpen: s.panelOpen, versions: s.versions, loading: s.loading, error: s.error
  })));
  const ref = useRef(null);
  const [toDelete, setToDelete] = useState(null);
  const close = () => useVersionStore.getState().closePanel();
  useFocusTrap(ref, panelOpen && !toDelete, close);

  if (!panelOpen) return null;
  const named = versions.filter(v => v.kind === 'named');
  const auto = versions.filter(v => v.kind === 'auto');

  const view = async (v) => {
    try {
      await useVersionStore.getState().startPreview(v);
      close();
    } catch (err) {
      toast.error(err.message || 'Stand konnte nicht geladen werden.');
    }
  };
  const confirmDelete = async () => {
    const v = toDelete;
    setToDelete(null);
    try { await useVersionStore.getState().deleteVersion(v.id); }
    catch (err) { toast.error(err.message || 'Löschen fehlgeschlagen.'); }
  };
  const nameOf = (v) => (v?.kind === 'named' ? v.label : v ? formatListStamp(v.created_at) : '');

  return (
    <div className="fixed inset-0 z-[90]" onClick={close}>
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="versions-title"
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full bg-surface border-l border-line shadow-2xl flex flex-col"
        style={{ width: 360, maxWidth: '100vw' }}
      >
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <h2 id="versions-title" className="text-sm font-bold text-ink flex items-center gap-2"><History size={15} aria-hidden="true" /> Versionen</h2>
          <button type="button" onClick={close} aria-label="Schließen" className="p-1 text-ink-3 hover:text-ink"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading && <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-ink-3" aria-label="Lädt" /></div>}
          {!loading && error && (
            <div role="alert" className="text-xs text-err px-2 py-3">
              {error}
              <button type="button" onClick={() => useVersionStore.getState().fetchVersions()} className="block mt-2 font-bold text-brand hover:underline">Erneut laden</button>
            </div>
          )}
          {!loading && !error && versions.length === 0 && (
            <p className="text-xs text-ink-4 px-2 py-6 text-center">Noch keine Stände. Die App sichert automatisch, sobald du länger an der Berechnung arbeitest.</p>
          )}
          {!loading && !error && named.length > 0 && (
            <section className="mb-4">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-ink-4 px-3 mb-1">Benannte Stände</h3>
              <ul>{named.map(v => <Entry key={v.id} v={v} onView={view} onDelete={setToDelete} />)}</ul>
            </section>
          )}
          {!loading && !error && auto.length > 0 && (
            <section>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-ink-4 px-3 mb-1">Automatische Stände</h3>
              <ul>{auto.map(v => <Entry key={v.id} v={v} onView={view} onDelete={setToDelete} />)}</ul>
            </section>
          )}
        </div>
      </aside>
      <div onClick={(e) => e.stopPropagation()}>
        <ConfirmDialog
          open={!!toDelete}
          title="Stand löschen?"
          message={`Stand „${nameOf(toDelete)}“ löschen? Das lässt sich nicht rückgängig machen.`}
          confirmLabel="Löschen"
          onConfirm={confirmDelete}
          onCancel={() => setToDelete(null)}
          variant="red"
        />
      </div>
    </div>
  );
}
